<script lang="ts">
  // components
  import Photos from './Photos.svelte';

  // utils
  import { intcomma } from 'journalize';
  import { formatPercent } from '../utils/format.js';
  import {
    choiceName,
    isRunoff,
    nameParts,
    isWinner,
    measureLabel,
    partyClass,
  } from '../utils/race-helpers.js';

  // types
  import type { Choice, CrosswalkImage } from '../types';

  export let candidate: Choice;
  export let totalVotes: number;
  export let isBallotMeasure: boolean;
  export let hasParties: boolean;
  export let face = false;
  export let hasDroppedOut = false;
  /** Headshot; undefined when the crosswalk has no entry (not even 'person') */
  export let lookup: CrosswalkImage | undefined = undefined;

  function slugify(text: string) {
    return text
      .toString()
      .toLowerCase()
      .replace(/\s+/g, '-') // Replace spaces with -
      .replace(/[^\w\-]+/g, '') // Remove all non-word chars
      .replace(/\-\-+/g, '-') // Replace multiple - with single -
      .replace(/^-+/, '') // Trim - from start of text
      .replace(/-+$/, ''); // Trim - from end of text
  }

  // reactive so the row updates when the feed polls
  // displayed separately so the last name can be shown alone where space is tight
  $: ({ first, middle, last } = nameParts(candidate));
  // measures only: 'Yes' / 'No', the same for AP and L.A. Vote
  $: title = measureLabel(candidate);
  // one string, only for the alt text and slug classes
  $: name = choiceName(candidate);
  $: votes = candidate.votes;
  $: winner = isWinner(candidate);
  $: runoff = isRunoff(candidate);
  $: isIncumbent = candidate.isIncumbent;
  // 'dem' | 'gop' | 'np'... or 'yes' / 'no' for measures, for the styles
  $: party = partyClass(candidate);
  // screen reader label for the checkmark; a winning Yes means the measure passed
  $: winnerText = party === 'yes' ? 'Passed' : party === 'no' ? 'Failed' : 'winner';
</script>

{#if face}
  <td class="candidate__cell">
    <div class="candidate__cell-headshot {slugify(name)} {party}"
    class:dropped={hasDroppedOut}>
      {#if lookup}
        <Photos
          name={name}
          src={lookup.jpg || lookup.png}
          avif={lookup.avif}
          webp={lookup.webp}
          {party}
        />
      {/if}
    </div>
  </td>
{/if}
<td class="candidate__cell candidate__cell-bar">
  <!-- MOBILE ONLY STYLE -->
  <div
    class="mobile-only text-uppercase bar {!face ? 'bar-'+party : ''} senate-bar_{slugify(name)}"
  >
    {#if winner}
      <div class="candidate__cell-winner candidate__{party}" role="img" aria-label={winnerText}> ✓ </div>
    {/if}
  </div>
  <!-- DESKTOP ONLY STYLE -->
  <div
    class="desktop-only bar {!face ? 'bar-'+party : ''} senate-bar_{slugify(name)}"
  >
    {#if winner}
      <div class="candidate__cell-winner candidate__{party}" aria-hidden="true"> ✓ </div>
    {/if}
  </div>
</td>
  <!--NAME-->

<th
scope="row"
class="candidate__cell candidate__cell-name"
class:candidate__cell-name-winner={winner || runoff}
class:dropped={hasDroppedOut}
>
{#if hasDroppedOut}
<span class="candidate__cell-dropped candidate__{party}">
  Dropped out
</span><br>
{/if}
{#if isBallotMeasure}{title}{:else}{first} {#if middle}{middle} {/if}{last}{/if}&nbsp;{#if isIncumbent}
  <span
    class="candidate__cell-incumbent"
    title="Currently holds this office"
    role="img"
    aria-label="incumbent">*</span
  >
{/if}
{#if !isBallotMeasure && hasParties && candidate.party}
  <span class="candidate__cell-name-party">({candidate.party.abbr})</span>
{/if}
{#if runoff}
<span
  class="candidate__cell-runoff candidate__{party}">
  Runoff
  </span>
{/if}
</th>
<td
class="candidate__cell candidate__cell-pct {winner ? 'text-medium' : ''} text-left"
class:candidate__cell-ballot-measures={isBallotMeasure}
>
{formatPercent(votes, totalVotes)}<span class="candidate__pct-symbol">%</span>
</td>
<td
class="candidate__cell candidate__cell-votes text-right"
class:candidate__cell-ballot-measures={isBallotMeasure}
>
{intcomma(votes)}
</td>
